import { useEffect, useMemo, useRef, useState } from "react";
import BasicInfoFields from "./BasicInfoFields";
import LocationFields from "./LocationFields";
import {
  objectToOptions,
  arrayToOptions,
  hasShallowChanges,
  formatTinNo,
} from "./utils";
import regionPh from "../../../../../components/regionsPh.json";
import provincePh from "../../../../../components/provincePh.json";
import municipalityPh from "../../../../../components/municipalityPh.json";
import barangayPh from "../../../../../components/barangayPh.json";

function SuppliersForm({
  onSubmit,
  onBack,
  object,
  discardModal,
  hasActualChanges,
  onChange: onParentChange,
  ...props
}) {
  const [formData, setFormData] = useState({
    name: "",
    firstName: "",
    middleName: "",
    lastName: "",
    isVatable: false,
    tinNo: "",
    region: "",
    province: "",
    city: "",
    barangay: "",
    fullAddress: "",
  });
  const [provincesOptions, setProvinces] = useState([]);
  const [citiesOptions, setCities] = useState([]);
  const [barangayOptions, setBarangay] = useState([]);
  const regionsOptions = useMemo(() => objectToOptions(regionPh), []);
  const provinceInput = useRef();
  const cityInput = useRef();
  const barangayInput = useRef();

  useEffect(() => {
    if (!object) return;
    setFormData({
      ...object,
      name: object.name || "",
      isVatable: object.isVatable || false,
      tinNo: object.tinNo || "",
      region: object.region || "",
      province: object.province || "",
      city: object.city || "",
      barangay: object.barangay || "",
      fullAddress: object.fullAddress || "",
    });

    if (object.region) {
      const selectedRegion = regionPh.find(
        (r) => r.regionName === object.region,
      );
      if (selectedRegion) {
        const provinces = provincePh.filter(
          (p) => p.regionCode === selectedRegion.code,
        );
        const cities = municipalityPh.filter(
          (c) => c.regionCode === selectedRegion.code,
        );
        setProvinces(arrayToOptions(provinces));

        setCities(arrayToOptions(cities));
      }
    }
    if (object.province) {
      const selectedProvince = provincePh.find(
        (p) => p.name === object.province,
      );
      if (selectedProvince) {
        const cities = municipalityPh.filter(
          (c) => c.provinceCode === selectedProvince.code,
        );
        setCities(arrayToOptions(cities));
      }
    }
    if (object.city) {
      const selectedCity = municipalityPh.find((c) => c.name === object.city);
      if (selectedCity) {
        const barangays = barangayPh.filter(
          (b) => b.municipalityCode === selectedCity.code,
        );
        setBarangay(arrayToOptions(barangays));
      }
    }
  }, [object]);

  const handleChange = (value, field) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (field !== "tinNo") onParentChange?.(value, field);

    if (field === "region") {
      if (value === "") {
        provinceInput.current.onClickClear();
        cityInput.current.onClickClear();
        barangayInput.current.onClickClear();
      }
      const selectedRegion = regionPh.find((r) => r.regionName === value);

      if (selectedRegion) {
        const provinces = provincePh.filter(
          (p) => p.regionCode === selectedRegion.code,
        );
        const cities = municipalityPh.filter(
          (m) => m.regionCode === selectedRegion.code,
        );

        if (provinces.length > 0) {
          setProvinces(arrayToOptions(provinces));
          setCities([]);
        } else {
          setProvinces([]);
          setCities(arrayToOptions(cities));
        }
      } else {
        setProvinces([]);
        setCities([]);
      }

      setBarangay([]);

      setFormData((prev) => ({
        ...prev,
        province: "",
        city: "",
        barangay: "",
      }));
    }

    if (field === "province") {
      if (value === "") {
        cityInput.current.onClickClear();
        barangayInput.current.onClickClear();
      }
      const selectedProvince = provincePh.find((p) => p.name === value);
      if (selectedProvince) {
        const cities = municipalityPh.filter(
          (c) => c.provinceCode === selectedProvince.code,
        );
        setCities(arrayToOptions(cities));
      }
      setBarangay([]);
      setFormData((prev) => ({ ...prev, city: "", barangay: "" }));
    }
    if (field === "city") {
      if (value === "") {
        barangayInput.current.onClickClear();
      }
      const selectedCity = municipalityPh.find((c) => c.name === value);
      if (selectedCity) {
        const barangays = barangayPh.filter((b) =>
          b.municipalityCode
            ? b.municipalityCode === selectedCity.code
            : b.cityCode === selectedCity.code,
        );
        setBarangay(arrayToOptions(barangays));
      }
      setFormData((prev) => ({ ...prev, barangay: "" }));
    }
    if (field === "tinNo") {
      const formatted = formatTinNo(value);
      setFormData((prev) => ({
        ...prev,
        [field]: formatted,
      }));
      onParentChange?.(formatted, field);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const _onCancel = () => {
    const hasChanges =
      (hasActualChanges && hasActualChanges()) ||
      hasShallowChanges(formData, object);
    if (hasChanges) {
      return discardModal();
    }

    onBack();
  };

  return (
    <div className="m-0 bg-white">
      <form
        onSubmit={handleSubmit}
        className="d-flex flex-column gap-3 justify-content-between"
      >
        <BasicInfoFields
          formData={formData}
          onChange={handleChange}
          {...props}
        />

        <LocationFields
          formData={formData}
          onChange={handleChange}
          regionsOptions={regionsOptions}
          provincesOptions={provincesOptions}
          citiesOptions={citiesOptions}
          barangayOptions={barangayOptions}
          provinceRef={provinceInput}
          cityRef={cityInput}
          barangayRef={barangayInput}
          {...props}
        />
        <div className="d-flex gap-2 mt-4">
          <button
            type="button"
            className="btn btn-light fw-medium border px-3 flex-fill"
            onClick={() => _onCancel()}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary fw-medium px-3 flex-fill"
            disabled={!hasActualChanges()}
          >
            <i className="bi bi-check-lg fw-bold"></i>{" "}
            {!formData?.id ? "Submit" : "Update"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default SuppliersForm;
